import { AsyncPipe, LowerCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Device } from '@capacitor/device';
import { Platform } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DswButtonModule } from '@vi-succ/design-system-web';
import { Catalog } from '@vi-succ/products-domain';
import { ShippingItem, ShoppingCart, ShoppingCartFacade } from '@vi-succ/shoppingcart-domain';
import { NGXLogger } from 'ngx-logger';
import { Observable, distinctUntilChanged, map, tap } from 'rxjs';
import { countryToSalesOrg } from '../../../../util';
import { SwitchConfirmationComponent } from '../../switch-confirmation/switch-confirmation.component';

export interface SalesOrg {
  value: string;
  languages: Language[];
}

export interface Language {
  value: string;
}

@Component({
  selector: 'et-catalog-switcher',
  templateUrl: './catalog-switcher.component.html',
  styleUrls: ['./catalog-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    DswButtonModule,
    AsyncPipe,
    LowerCasePipe,
    TranslateModule,
  ],
})
export class CatalogSwitcherComponent implements AfterViewInit {
  @Input() salesOrgs: { [key: string]: SalesOrg } = {};
  @Input() saveButtonLabel!: string;
  @Input() currentCatalog!: Catalog | null;
  @Output() selectCatalog = new EventEmitter<Catalog>();
  @Output() cancelClicked = new EventEmitter<void>();

  isDesktop = this.platform.is('desktop');
  shoppingCartContent$: Observable<ShoppingCart> = this.shoppingCartFacade.shoppingCartContent$;
  salesOrgControl = new FormControl('', [Validators.required]);
  languageControl = new FormControl('', [Validators.required]);
  currentLanguages$ = this.salesOrgControl.valueChanges.pipe(
    distinctUntilChanged(),
    map((salesOrg) => (salesOrg ? this.salesOrgs[salesOrg]?.languages : [])),
    tap((languages) => {
      if (languages.length > 0) {
        this.languageControl.setValue(languages[0].value);
      }
    })
  );
  form = new FormGroup({
    salesOrg: this.salesOrgControl,
    language: this.languageControl,
  });

  constructor(
    private platform: Platform,
    private translate: TranslateService,
    private shoppingCartFacade: ShoppingCartFacade,
    private dialog: MatDialog,
    private logger: NGXLogger
  ) {}

  get sortedSalesOrgs() {
    // Sort sales orgs alphabetically by their translated name
    return Object.values(this.salesOrgs).sort((a, b) =>
      this.translate
        .instant('catalog.salesorg.' + a.value.toLowerCase())
        .localeCompare(this.translate.instant('catalog.salesorg.' + b.value.toLowerCase()))
    );
  }

  async ngAfterViewInit() {
    if (this.currentCatalog) {
      this.form.patchValue({
        salesOrg: this.currentCatalog.salesOrg,
        language: this.currentCatalog.language,
      });
    } else {
      const { salesOrg, language } = await this.findCatalogPreselection();
      this.form.patchValue({
        salesOrg,
        language,
      });
    }
  }

  onSubmit(items: ShippingItem[]) {
    const catalog = this.form.value as Catalog;

    if (items.length === 0 || !this.hasSalesOrgChanged()) {
      this.selectCatalog.emit(catalog);
    } else {
      const dialogRef = this.dialog.open(SwitchConfirmationComponent);
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.selectCatalog.emit(catalog);
        }
      });
    }
  }

  private async findCatalogPreselection() {
    const tag = await Device.getLanguageTag();
    const tagValue = tag.value;
    // Check if tag matches xx-XX format
    if (/^[a-z]{2}-[A-Z]{2}$/.test(tagValue)) {
      this.logger.info('Device language tag: ' + tagValue);
      const language = tagValue.substring(0, 2);
      const country = tagValue.substring(3);
      // Check if tag matches any of the sales orgs
      const salesOrgTag = countryToSalesOrg(country);
      const salesOrg = this.sortedSalesOrgs.find((so) => so.value === salesOrgTag);
      if (salesOrg) {
        this.logger.info(`Found valid sales org: ${salesOrg.value}`);
        const languageTag = salesOrg.languages.find((l) => l.value.toLowerCase() === language.toLowerCase());
        if (languageTag) {
          this.logger.info(`Found valid language: ${languageTag.value}. Setting form values for salesorg and language`);
          return {
            salesOrg: salesOrg.value,
            language: languageTag.value,
          };
        } else {
          this.logger.info(
            `No valid language found for sales org ${salesOrg.value}. Setting form values for salesorg and first language`
          );
          return {
            salesOrg: salesOrg.value,
            language: salesOrg.languages[0].value,
          };
        }
      }
    } else {
      this.logger.info('Device language tag does not match xx-XX format: ' + tagValue);
    }
    return {
      salesOrg: null,
      language: null,
    };
  }

  public hasSalesOrgChanged(): boolean {
    return this.currentCatalog?.salesOrg !== this.form.value.salesOrg;
  }

  onCancel() {
    this.cancelClicked.emit();
  }
}
